/**
 * @unscoped — 워크스페이스 파일 유형 분류 SSOT (프론트엔드 미러, 2026-08-12).
 *
 * ⚠ **서버 미러**: apps/chat-server/src/modules/workspace/file-kind.ts 가 원본이다.
 *   chat-server 는 ESM/CJS interop 문제로 packages/shared-types 를 직접 import 하지 않아
 *   이 레포는 "미러 + 드리프트 스크립트" 를 관례로 쓴다. 표를 고치면 반드시 양쪽을
 *   함께 고치고 `pnpm check:file-kind` 로 확인할 것.
 *
 * 프론트가 이 표를 갖는 이유는 두 가지다:
 *   ① 필터 칩 목록(전체/Word/Excel/PPT/그외)을 서버 라운드트립 없이 그린다.
 *   ② 파일 행 아이콘을 유형으로 고른다.
 */

// 2026-08-14 — 드라이브화로 필터가 5종 → 9종이 되었다(사용자 요구:
//   모두 / 워드 / 엑셀 / 파워포인트 / PDF / 마크업 / txt / 이미지 / 기타).
//   순서가 곧 필터 칩의 표시 순서다.
// 2026-08-14 (2차) — 사용자 요구로 **hwp 를 PPT 옆의 독립 칩**으로 뺀다(.hwp/.hwpx 둘 다).
//   그전에는 word 에 합쳐 두었는데, 한글 문서를 따로 걸러 보려면 Word 칩을 눌러 워드까지
//   함께 보는 수밖에 없었다. 아이콘은 지금까지와 같이 문서 모양·워드 색을 쓴다
//   (file-icon.ts·iOS·AOS 의 '카테고리' 분류는 그대로 — 저쪽은 아이콘 결정용이다).
export const FILE_KINDS = [
  'all', 'word', 'excel', 'ppt', 'hwp', 'pdf', 'markup', 'txt', 'image', 'etc',
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** 정확 매칭으로 분류되는 유형 — 'image' 는 prefix 라 아래 KIND_PREFIXES 가 담당한다. */
type ExactKind = Exclude<FileKind, 'all' | 'etc' | 'image'>;

/** 분류에 쓰이는 MIME 표 — 'etc' 는 아래 어디에도 안 걸리는 나머지 전부. */
export const KIND_MIMES: Readonly<Record<ExactKind, readonly string[]>> = {
  word: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/msword',                                                      // .doc (레거시)
    'application/vnd.ms-word.document.macroenabled.12',                        // .docm
  ],
  excel: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',       // .xlsx
    'application/vnd.ms-excel',                                                // .xls (레거시)
    'application/vnd.ms-excel.sheet.macroenabled.12',                          // .xlsm
  ],
  ppt: [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.ms-powerpoint',                                             // .ppt (레거시)
    'application/vnd.ms-powerpoint.presentation.macroenabled.12',                // .pptm
  ],
  // 한컴 한글 (2026-08-14) — 업로드 허용 MIME(files.routes.ts ALLOWED_DOCUMENT_MIMES)과 동일 집합.
  // ⚠ 이 목록은 word 에서 **옮겨 온** 것이다. 양쪽에 두면 kindOfMime 이 앞선 word 를 돌려줘
  //   hwp 칩이 영영 0건이 된다(그리고 '그외' 판정용 평탄화 표에 중복이 생긴다).
  hwp: [
    'application/x-hwp',                                                       // .hwp
    'application/haansofthwp',                                                 // .hwp (변종)
    'application/vnd.hancom.hwp',                                              // .hwp (변종)
    'application/hwp+zip',                                                     // .hwpx
    'application/vnd.hancom.hwpx',                                             // .hwpx (변종)
  ],
  pdf: [
    'application/pdf',
  ],
  markup: [
    'text/markdown',
    'text/x-markdown',
  ],
  txt: [
    'text/plain',
  ],
};

/**
 * prefix 로 분류되는 유형.
 *
 * 이미지는 MIME 이 image/png · image/jpeg · image/webp · image/heic … 로 끝없이 늘어난다.
 * 정확 매칭 표로 관리하면 새 포맷이 나올 때마다 조용히 '기타' 로 떨어지므로 prefix 로 본다.
 */
export const KIND_PREFIXES: Readonly<Record<'image', string>> = {
  image: 'image/',
};

function normalize(mime: string | null | undefined): string {
  return (mime ?? '').toLowerCase().split(';')[0]!.trim();
}

/** 이 MIME 이 속하는 유형 (pure). 폴더/미상은 'etc'. */
export function kindOfMime(mime: string | null | undefined): Exclude<FileKind, 'all'> {
  const m = normalize(mime);
  if (m === '') return 'etc';
  for (const kind of ['word', 'excel', 'ppt', 'hwp', 'pdf', 'markup', 'txt'] as const) {
    if (KIND_MIMES[kind].includes(m)) return kind;
  }
  if (m.startsWith(KIND_PREFIXES.image)) return 'image';
  return 'etc';
}

/** 문자열이 유효한 유형인지. */
export function isFileKind(value: string): value is FileKind {
  return (FILE_KINDS as readonly string[]).includes(value);
}
